// PROD-3310 billing bridge.
// Mirrors: dev-scrips-pm-react src/components/ui/calendar.tsx (react-day-picker `DayPicker` with PM's classNames:
// dropdown caption, 40px day cells on the 8px radius, the brand fill on a selected day or range end, the blue-100 band
// across a range, hint ink outside the month; years 1900-2100).
// Wraps:   NO Signal DS component — the DS exports no inline calendar (only `DatePicker` / `DateRangePicker`, a field
// with a popover calendar whose internals `calendarClassNames` / `CalendarDayButton` are not exported). This renders
// `react-day-picker`, the same engine the DS pickers use, with PM's classes translated to named Signal DS utilities.
// Overrides: PM's arbitrary-value classes (radius literals, `var(--color-…)` inks, the cell-size custom property)
// become the named utilities that resolve to the same tokens. PROD-3310 gap: a DS inline Calendar.
import { useEffect, useRef, type ComponentProps } from 'react';
import { DayPicker, getDefaultClassNames, type DayButton } from 'react-day-picker';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const PICKER_MIN_YEAR = 1900;
const PICKER_MAX_YEAR = 2100;

const NAV_BUTTON =
  'inline-flex items-center justify-center h-8 w-8 rounded-md text-text-muted hover:bg-grey-100 disabled:opacity-40 disabled:pointer-events-none';

export type CalendarProps = ComponentProps<typeof DayPicker>;

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  formatters,
  components,
  startMonth,
  endMonth,
  ...props
}: CalendarProps) {
  const d = getDefaultClassNames();
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      startMonth={startMonth ?? new Date(PICKER_MIN_YEAR, 0)}
      endMonth={endMonth ?? new Date(PICKER_MAX_YEAR, 11)}
      captionLayout={captionLayout}
      className={cn('bg-surface-card p-2', className)}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(undefined, { month: 'long' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', d.root),
        months: cn('relative flex flex-col', d.months),
        month: cn('flex flex-col gap-3', d.month),
        nav: cn('absolute inset-x-0 top-0 flex items-center justify-between h-10', d.nav),
        button_previous: cn(NAV_BUTTON, d.button_previous),
        button_next: cn(NAV_BUTTON, d.button_next),
        month_caption: cn('flex h-10 items-center justify-center', d.month_caption),
        dropdowns: cn('flex items-center justify-center gap-2', d.dropdowns),
        dropdown_root: cn(
          'relative inline-flex items-center h-8 rounded-md border border-border-strong bg-surface-card',
          d.dropdown_root,
        ),
        dropdown: cn('absolute inset-0 w-full h-full opacity-0 cursor-pointer', d.dropdown),
        caption_label: cn(
          'select-none text-size-sm font-medium text-text-primary',
          captionLayout === 'label' ? '' : 'pointer-events-none flex items-center gap-1 px-3',
          d.caption_label,
        ),
        weekdays: cn('flex', d.weekdays),
        weekday: cn(
          'h-8 w-10 flex items-center justify-center text-size-xs font-normal text-text-muted select-none',
          d.weekday,
        ),
        week: cn('flex mt-1', d.week),
        day: cn('p-0 text-center', d.day),
        range_start: cn('bg-blue-100 rounded-l-md', d.range_start),
        range_middle: cn('bg-blue-100', d.range_middle),
        range_end: cn('bg-blue-100 rounded-r-md', d.range_end),
        outside: cn('text-text-hint', d.outside),
        disabled: cn('text-text-hint opacity-40', d.disabled),
        hidden: cn('invisible', d.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClass }) => {
          const Glyph = orientation === 'left' ? ChevronLeft : orientation === 'right' ? ChevronRight : ChevronDown;
          return <Glyph className={cn('size-4', chevronClass)} aria-hidden />;
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}

export function CalendarDayButton({ className, day, modifiers, ...props }: ComponentProps<typeof DayButton>) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const isEndpoint =
    modifiers.range_start || modifiers.range_end || (modifiers.selected && !modifiers.range_middle && !modifiers.outside);

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString()}
      data-endpoint={isEndpoint || undefined}
      className={cn(
        'flex items-center justify-center h-10 w-10 rounded-md text-size-sm font-normal text-text-primary',
        'transition-colors duration-100 outline-none hover:bg-grey-100 focus-visible:ring-2 focus-visible:ring-blue-400',
        modifiers.today &&
          !isEndpoint &&
          !modifiers.range_middle &&
          'font-semibold text-brand-primary ring-1 ring-inset ring-border-strong',
        modifiers.range_middle && 'rounded-none bg-transparent hover:bg-blue-100',
        isEndpoint && 'bg-brand-primary text-text-inverse hover:bg-brand-primary ring-0',
        modifiers.outside && 'text-text-hint',
        modifiers.disabled && 'text-text-hint opacity-40 pointer-events-none hover:bg-transparent',
        className,
      )}
      {...props}
    />
  );
}
